import { z } from 'zod'
import { stringSizeMaxMessage } from '@Entities/schemas/work/error-messages'
import { addressFormKey, addressSchema } from '@Entities/schemas/common/address-schema'
import { REQUIRED_MESSAGE } from '@Const/error-messages'
import { phoneNumberFormKey, phoneNumberSchema } from '@Entities/schemas/common/phone-number-schema'
import populateSchema from '@App/forms/v2/helpers/populate-schema'
import { email } from '@Entities/schemas/rightsholder/common-fields'
import { IPIValidator, IsniValidator } from '@App/helpers/validate'
import { buildCreateRightsholderFormFieldsNoSchema } from '@Entities/schemas/rightsholder/create-rightsholder-form-fields'

const MemberTypeSchema = z.union([
    z.literal('Songwriter'),
    z.literal('Publisher'),
    z.literal('CMO'),
    z.literal('Administrator'),
    z.literal('Manager/Lawyer/Other'),
    z.literal(''),
])

type MemberTypeType = z.infer<typeof MemberTypeSchema>

const MemberSubTypeSchema = z.union([
    z.literal('Self Administered'),
    z.literal('Partially Administered'),
    z.literal('Fully Administered'),
    z.literal("I don't know"),
    z.literal(''),
])

type MemberSubTypeType = z.infer<typeof MemberSubTypeSchema>

const createRightsholderFormSchema = z.object({
    name: z.string().min(1, REQUIRED_MESSAGE).max(200, stringSizeMaxMessage('Name', 200)),
    [addressFormKey]: addressSchema,
    [phoneNumberFormKey]: phoneNumberSchema,
    email: email,
    memberType: MemberTypeSchema,
    memberSubType: MemberSubTypeSchema,
    ipiNumber: z
        .string()
        .max(11, stringSizeMaxMessage('IPI Number', 11))
        .refine((ipi) => IPIValidator(ipi, false), 'Invalid IPI Number')
        .optional(),
    isni: z
        .string()
        .refine((isni) => IsniValidator(isni, false), 'Invalid ISNI')
        .optional(),
    hfaPublisherNumber: z.string().max(6, stringSizeMaxMessage('Publisher Account Number', 6)).optional(),
    termsAndConditions: z
        .boolean({
            error: REQUIRED_MESSAGE,
        })
        .refine((val) => val, {
            message: REQUIRED_MESSAGE,
        }),
})

const createRightsholderQuestionnaireFormSchema = z.object({
    question1: z.string(),
    question2: z.string(),
})

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const createRightsholderQuestionnaireSchema = z.object({
    questionAnswers: z.array(
        z.object({
            userId: z.string(),
            questionId: z.number(),
            answer: z.string(),
        })
    ),
})

type CreateRightsholderForm = z.infer<typeof createRightsholderFormSchema>
type CreateRightsholderQuestionnaireForm = z.infer<typeof createRightsholderQuestionnaireFormSchema>
type CreateRightsholderQuestionnaire = z.infer<typeof createRightsholderQuestionnaireSchema>

const createRightsholderFormFields = populateSchema(
    buildCreateRightsholderFormFieldsNoSchema(),
    createRightsholderFormSchema
)

export {
    createRightsholderFormSchema,
    CreateRightsholderForm,
    createRightsholderFormFields,
    CreateRightsholderQuestionnaireForm,
    createRightsholderQuestionnaireFormSchema,
    CreateRightsholderQuestionnaire,
    MemberTypeType,
    MemberSubTypeType,
}
